import { Fragment, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import {
  Extension,
  Node,
  mergeAttributes,
  wrappingInputRule,
  type ChainedCommands,
  type Editor,
  type Extensions,
  type MarkdownParseHelpers,
} from "@tiptap/core"
import Bold from "@tiptap/extension-bold"
import Code from "@tiptap/extension-code"
import Document from "@tiptap/extension-document"
import HardBreak from "@tiptap/extension-hard-break"
import Heading from "@tiptap/extension-heading"
import Italic from "@tiptap/extension-italic"
import Link from "@tiptap/extension-link"
import { BulletList, ListItem, ListKeymap, OrderedList } from "@tiptap/extension-list"
import Paragraph from "@tiptap/extension-paragraph"
import Strike from "@tiptap/extension-strike"
import { Table, TableCell, TableHeader, TableRow, renderTableToMarkdown } from "@tiptap/extension-table"
import Text from "@tiptap/extension-text"
import { Gapcursor, Placeholder, TrailingNode, UndoRedo } from "@tiptap/extensions"
import { Markdown } from "@tiptap/markdown"
import type { EditorView } from "@tiptap/pm/view"
import { EditorContent, useEditor, useEditorState } from "@tiptap/react"
import {
  BetweenHorizontalEndIcon,
  BetweenVerticalEndIcon,
  BoldIcon,
  CodeIcon,
  Columns3Icon,
  Grid2x2XIcon,
  Heading2Icon,
  ItalicIcon,
  ListIcon,
  ListOrderedIcon,
  Rows3Icon,
  StrikethroughIcon,
  TableIcon,
  TextQuoteIcon,
  type LucideIcon,
} from "lucide-react"
import { insertDictatedText } from "@/components/prompt-editor/dictation-insert"
import { InlineMentionCompletion } from "@/components/prompt-editor/inline-mention-completion"
import type { MentionAsyncItems } from "@/components/prompt-editor/mention-list"
import { createMentionSuggestion, filterMentionItems } from "@/components/prompt-editor/suggestion"
import { mentionDisplayLabel, type MentionCategory, type MentionItem } from "@/components/prompt-editor/types"
import { TaskDescriptionDiff, setTaskDescriptionDiff } from "@/components/tasks/task-description-diff"
import { TaskFilePastePlaceholder, hasPendingFileUploads, insertPastedFiles } from "@/components/tasks/task-file-paste"
import { TaskMention, taskMentionLabelsTransaction, type TaskMentionLabelLookup } from "@/components/tasks/task-mention"
import { TaskMentionsContext, useTaskMentionTexts, type TaskMentionsValue } from "@/components/tasks/task-mentions"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { VoiceDictationButton } from "@/components/voice/voice-dictation-button"
import { useTranslation } from "@/hooks/use-translation"
import type { AgentApiScope } from "@/lib/agent-api"
import { clipboardHasFiles, filesFromClipboard } from "@/lib/clipboard-files"
import { taskPersonLabel, taskPersonName } from "@/lib/task-board"
import {
  TASK_MENTION_ENTITY_KINDS,
  isTaskMentionEntityKind,
  resolveTaskMentionEntity,
  taskMentionDirectory,
  taskMentionDirectoryKey,
  taskMentionDisplay,
  taskMentionEntityDisplay,
  taskMentionEntityLabel,
  taskMentionIds,
  type TaskMentionDirectory,
  type TaskMentionTexts,
} from "@/lib/task-mentions"
import { cn } from "@/lib/utils"

export type TaskDescriptionEditorProps = {
  value: string
  onChange?: (markdown: string) => void
  onBaseline?: (markdown: string) => void
  onBlur?: () => void
  onModEnter?: () => void
  editable: boolean
  placeholder?: string
  variant: "inline" | "field" | "comment" | "composer" | "studio"
  autoFocus?: boolean
  ariaLabel?: string
  className?: string
  fileMentions?: MentionItem[]
  onFileMentionOpen?: (item: MentionItem) => void
  onPasteFiles?: (files: File[]) => Promise<MentionItem[]> | null
  // Скрепка снаружи редактора ставит чипы загружаемых файлов на место курсора, как вставка.
  insertFilesRef?: RefObject<TaskEditorFileInserter | null>
  insertTextRef?: RefObject<TaskEditorTextInserter | null>
  onUploadingChange?: (uploading: boolean) => void
  actions?: ReactNode
  leading?: ReactNode
  diffBase?: string
  editorHandleRef?: RefObject<TaskEditorHandle | null>
  /** Голосовой ввод в каретку. Кнопка в правом нижнем углу поля, только в правке описания (вариант inline). */
  dictation?: {
    projectId: number
    agentScope?: AgentApiScope
  }
  /**
   * Фон липкой панели форматирования (вариант inline): цвет своей поверхности,
   * чтобы полоса не отличалась от ящика заметок или пика задачи.
   */
  stickyToolbarClassName?: string
}

export type TaskEditorHandle = {
  markdown: () => string
  selectionMarkdown: () => string
  replaceSelection: (markdown: string) => void
  undo: () => void
}

export type TaskEditorFileInserter = (upload: Promise<MentionItem[]>) => void
export type TaskEditorTextInserter = (text: string) => void

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent)

const MAC_KEYS: Record<string, string> = { Mod: "⌘", Shift: "⇧", Alt: "⌥", Enter: "↵" }

export function shortcutHint(...keys: string[]): string {
  if (IS_MAC) return keys.map((key) => MAC_KEYS[key] ?? key).join("")
  return keys.map((key) => (key === "Mod" ? "Ctrl" : key)).join("+")
}

const SafeLink = Link.extend({
  addAttributes() {
    return {
      href: { default: null, parseHTML: (element: HTMLElement) => element.getAttribute("href") },
      title: { default: null },
    }
  },
})

const renderParagraph = Paragraph.config.renderMarkdown

function escapeBlockMarkers(markdown: string): string {
  return markdown
    .replace(/^(\s*)(\d+)([.)])(?=\s|$)/gm, "$1$2\\$3")
    .replace(/^(\s*)(#{1,6}(?=\s|$)|[-+*](?=\s|$)|>|-{2,}\s*$|=+\s*$|\*{3,}\s*$)/gm, "$1\\$2")
}

// Строка «1) …» или «# …», набранная как обычный текст, после перезагрузки не должна стать списком или заголовком.
const MarkdownParagraph = Paragraph.extend({
  renderMarkdown: (node, h, ctx) => escapeBlockMarkers(renderParagraph?.(node, h, ctx) ?? ""),
})

// Своя цитата: пакет @tiptap/extension-blockquote тянет вторую копию ProseMirror.
const Quote = Node.create({
  name: "blockquote",
  content: "block+",
  group: "block",
  defining: true,
  parseHTML: () => [{ tag: "blockquote" }],
  renderHTML: ({ HTMLAttributes }) => ["blockquote", mergeAttributes(HTMLAttributes), 0],
  markdownTokenName: "blockquote",
  parseMarkdown: (token, h) =>
    h.createNode("blockquote", undefined, (h.parseBlockChildren ?? h.parseChildren)(token.tokens ?? [])),
  renderMarkdown: (node, h) =>
    (node.content ?? [])
      .map((child) =>
        h
          .renderChildren([child])
          .split("\n")
          .map((line) => (line.trim() === "" ? ">" : `> ${line}`))
          .join("\n"),
      )
      .join("\n>\n"),
  addKeyboardShortcuts() {
    return { "Mod-Shift-b": () => this.editor.commands.toggleWrap(this.name) }
  },
  addInputRules() {
    return [wrappingInputRule({ find: /^\s*>\s$/, type: this.type })]
  },
})

// В ячейке Markdown-таблицы помещается одна строка, а `|` в тексте нужно экранировать.
const MarkdownTable = Table.extend({
  renderMarkdown: (node, h) =>
    renderTableToMarkdown(node, {
      ...h,
      renderChildren: (nodes, separator) => h.renderChildren(nodes, separator).replace(/\|/g, "\\|"),
    }),
}).configure({ resizable: false })
const SingleLineTableCell = TableCell.extend({ content: "paragraph" })
const SingleLineTableHeader = TableHeader.extend({ content: "paragraph" })

function paragraphFromText(text: string | undefined, h: MarkdownParseHelpers) {
  const content = text?.replace(/\s+$/, "")
  return h.createNode("paragraph", undefined, content ? [h.createTextNode(content)] : [])
}

// Без CodeBlock и HorizontalRule их токены иначе молча выпадают из документа.
const CodeBlockAsText = Extension.create({
  name: "taskDescriptionCodeBlockAsText",
  markdownTokenName: "code",
  parseMarkdown: (token, h) => paragraphFromText(token.text, h),
})

const RuleAsText = Extension.create({
  name: "taskDescriptionRuleAsText",
  markdownTokenName: "hr",
  parseMarkdown: (token, h) => paragraphFromText(token.raw, h),
})

type MentionRefs = {
  files: RefObject<MentionItem[]>
  people: RefObject<MentionItem[]>
  entities: RefObject<MentionItem[]>
  directory: RefObject<TaskMentionDirectory>
  searchTasks: RefObject<TaskMentionsValue["searchTasks"]>
  searchCopy: RefObject<TaskMentionsValue["mentionSearch"]>
  texts: RefObject<TaskMentionTexts>
  onOpen: RefObject<((item: MentionItem) => void) | undefined>
  editable: RefObject<boolean>
}

const NO_MENTION_DIRECTORY = taskMentionDirectory()
const NO_MENTION_ITEMS: MentionItem[] = []
const TASK_MENU_ORDER: MentionCategory[] = ["person", "file", ...TASK_MENTION_ENTITY_KINDS]

function mentionLabelLookup(refs: MentionRefs): TaskMentionLabelLookup {
  return (category, id, stateId) => {
    if (category === "file") return refs.files.current.find((item) => item.id === id)?.label ?? ""
    if (isTaskMentionEntityKind(category)) {
      const ref = resolveTaskMentionEntity(refs.directory.current, category, id, stateId ?? null)
      return ref ? taskMentionEntityLabel(ref, refs.texts.current) : undefined
    }
    if (category === "person") {
      const person = refs.directory.current.people.get(id)
      return person ? taskPersonLabel(person, refs.texts.current.unnamed) : undefined
    }
    if (category !== "task" || !refs.directory.current.tasks.has(id)) return undefined
    return taskMentionDisplay(refs.directory.current, "task", id, "", refs.texts.current).text
  }
}

function createMentionExtensions(refs: MentionRefs, lookup: TaskMentionLabelLookup) {
  const menuItems = () => [...refs.files.current, ...refs.people.current, ...refs.entities.current]
  const asyncTasks: MentionAsyncItems = {
    load: (query, signal) => refs.searchTasks.current?.(query, signal) ?? Promise.resolve([]),
    get minQuery() {
      return refs.searchCopy.current?.minQuery ?? 3
    },
    get hint() {
      return refs.searchCopy.current?.hint ?? refs.texts.current.taskSearchHint
    },
    get loading() {
      return refs.searchCopy.current?.loading ?? refs.texts.current.taskSearching
    },
  }
  return [
    TaskMention.configure({
      getMentionItems: () => refs.files.current,
      getOnMentionOpen: () => refs.onOpen.current,
      getMentionLabel: () => lookup,
      getMentionDisplay: () => (category, id, label, stateId) => {
        if (category === "person" || category === "task") {
          const shown = taskMentionDisplay(refs.directory.current, category, id, label, refs.texts.current)
          if (category !== "person") return shown
          const record = refs.directory.current.people.get(id)
          return { ...shown, person: record ? taskPersonName(record) : null }
        }
        if (!isTaskMentionEntityKind(category)) return null
        return taskMentionEntityDisplay(
          refs.directory.current,
          category,
          id,
          stateId ?? null,
          label,
          refs.texts.current,
        )
      },
      getReplaceable: () => false,
      suggestion: {
        ...createMentionSuggestion(
          menuItems,
          () => "auto",
          () => undefined,
          {
            captureEscape: false,
            asyncItems: () => (refs.searchTasks.current ? asyncTasks : null),
            categoryOrder: TASK_MENU_ORDER,
          },
        ),
        // Лимит меню у каждой группы свой: файлы не вытесняют людей, герои — локации.
        items: ({ query }) => [
          ...filterMentionItems(refs.files.current, query),
          ...filterMentionItems(refs.people.current, query),
          ...TASK_MENTION_ENTITY_KINDS.flatMap((kind) =>
            filterMentionItems(
              refs.entities.current.filter((item) => item.category === kind),
              query,
            ),
          ),
        ],
        allow: () =>
          refs.files.current.length > 0 ||
          refs.people.current.length > 0 ||
          refs.entities.current.length > 0 ||
          refs.searchTasks.current != null,
      },
    }),
    InlineMentionCompletion.configure({
      getItems: () => (refs.editable.current ? refs.files.current : []),
      getOnMentionInsert: () => undefined,
    }),
    TaskFilePastePlaceholder,
  ]
}

function syncMentionLabels(editor: Editor, lookup: TaskMentionLabelLookup) {
  const tr = taskMentionLabelsTransaction(editor.state, lookup)
  if (tr) editor.view.dispatch(tr)
}

function withPastedFiles(files: MentionItem[], pasted: MentionItem[]): MentionItem[] {
  return [...files, ...pasted.filter((item) => !files.some((file) => file.id === item.id))]
}

// Схема и Markdown-парсер, общие с готовым HTML (task-markdown-html.tsx); чип файла добавляет вызывающий.
export function createTaskMarkdownExtensions(): Extensions {
  return [
    Document,
    MarkdownParagraph,
    Text,
    Bold,
    Italic,
    Strike,
    Code,
    Heading.configure({ levels: [2, 3] }),
    Quote,
    HardBreak,
    BulletList,
    OrderedList,
    ListItem,
    SafeLink.configure({
      autolink: true,
      linkOnPaste: true,
      openOnClick: false,
      defaultProtocol: "https",
      HTMLAttributes: { target: "_blank", rel: "noopener noreferrer nofollow" },
    }),
    MarkdownTable,
    TableRow,
    SingleLineTableHeader,
    SingleLineTableCell,
    CodeBlockAsText,
    RuleAsText,
    Markdown,
  ]
}

function createExtensions(
  placeholderRef: RefObject<string | undefined>,
  onModEnterRef: RefObject<(() => void) | undefined>,
) {
  return [
    ...createTaskMarkdownExtensions(),
    ListKeymap,
    Placeholder.configure({ placeholder: () => placeholderRef.current ?? "" }),
    UndoRedo,
    Gapcursor,
    TrailingNode,
    TaskDescriptionDiff,
    Extension.create({
      name: "taskDescriptionModEnter",
      priority: 1000,
      addKeyboardShortcuts() {
        return {
          "Mod-Enter": () => {
            const handler = onModEnterRef.current
            if (!handler) return false
            if (!hasPendingFileUploads(this.editor.state)) handler()
            return true
          },
        }
      },
    }),
  ]
}

function openLinkOnModClick(view: EditorView, event: MouseEvent): boolean {
  if (!view.editable || !(event.metaKey || event.ctrlKey) || !(event.target instanceof Element)) return false
  const anchor = event.target.closest("a")
  const href = anchor && view.dom.contains(anchor) ? anchor.getAttribute("href") : null
  if (!href) return false
  window.open(href, "_blank", "noopener,noreferrer")
  return true
}

function selectionMarkdown(editor: Editor): string {
  const { from, to, empty } = editor.state.selection
  if (empty) return ""
  const markdownApi = (editor as Editor & { markdown?: { serialize?: (content: unknown) => string } }).markdown
  if (markdownApi?.serialize) {
    try {
      return markdownApi.serialize(editor.state.doc.cut(from, to)).trim()
    } catch {
      return editor.state.doc.textBetween(from, to, "\n").trim()
    }
  }
  return editor.state.doc.textBetween(from, to, "\n").trim()
}

export function TaskDescriptionEditor({
  value,
  onChange,
  onBaseline,
  onBlur,
  onModEnter,
  editable,
  placeholder,
  variant,
  autoFocus = false,
  ariaLabel,
  className,
  fileMentions,
  onFileMentionOpen,
  onPasteFiles,
  insertFilesRef,
  insertTextRef,
  onUploadingChange,
  actions,
  leading,
  diffBase,
  editorHandleRef,
  dictation,
  stickyToolbarClassName,
}: TaskDescriptionEditorProps) {
  const { t } = useTranslation("tasks")
  const { t: tShot } = useTranslation("shot")
  const mentions = useContext(TaskMentionsContext)
  const mentionTexts = useTaskMentionTexts()
  const directory = mentions?.directory ?? NO_MENTION_DIRECTORY
  const onChangeRef = useRef(onChange)
  const onBaselineRef = useRef(onBaseline)
  const onBlurRef = useRef(onBlur)
  const onModEnterRef = useRef(onModEnter)
  const placeholderRef = useRef(placeholder)
  const fileMentionsRef = useRef<MentionItem[]>(fileMentions ?? [])
  const pastedFilesRef = useRef<MentionItem[]>([])
  const onFileMentionOpenRef = useRef(onFileMentionOpen)
  const onPasteFilesRef = useRef(onPasteFiles)
  const uploadingLabelRef = useRef("")
  const editableRef = useRef(editable)
  const peopleRef = useRef<MentionItem[]>(mentions?.people ?? [])
  const entitiesRef = useRef<MentionItem[]>(mentions?.entities ?? NO_MENTION_ITEMS)
  const directoryRef = useRef(directory)
  const searchTasksRef = useRef(mentions?.searchTasks ?? null)
  const searchCopyRef = useRef(mentions?.mentionSearch ?? null)
  const ensureTasksRef = useRef(mentions?.ensureTasks)
  const textsRef = useRef(mentionTexts)
  const emittedRef = useRef<string | null>(null)
  const focusedOnceRef = useRef(false)
  // Поле с автофокусом до первого фокуса считается активным: иначе при пересоздании оно мигнёт свёрнутым.
  const [focusPending, setFocusPending] = useState(autoFocus)
  const knownFiles = fileMentions ?? []
  onChangeRef.current = onChange
  onBaselineRef.current = onBaseline
  onBlurRef.current = onBlur
  onModEnterRef.current = onModEnter
  placeholderRef.current = placeholder
  // Вставленный файл нужен чипу сразу, до перезагрузки задачи; когда он придёт в fileMentions, копия уходит.
  pastedFilesRef.current = pastedFilesRef.current.filter((item) => !knownFiles.some((file) => file.id === item.id))
  fileMentionsRef.current = withPastedFiles(knownFiles, pastedFilesRef.current)
  onFileMentionOpenRef.current = onFileMentionOpen
  onPasteFilesRef.current = onPasteFiles
  uploadingLabelRef.current = t("files.uploading")
  editableRef.current = editable
  peopleRef.current = mentions?.people ?? []
  entitiesRef.current = mentions?.entities ?? NO_MENTION_ITEMS
  directoryRef.current = directory
  searchTasksRef.current = mentions?.searchTasks ?? null
  searchCopyRef.current = mentions?.mentionSearch ?? null
  ensureTasksRef.current = mentions?.ensureTasks
  textsRef.current = mentionTexts

  const { extensions, labelLookup } = useMemo(() => {
    const refs: MentionRefs = {
      files: fileMentionsRef,
      people: peopleRef,
      entities: entitiesRef,
      directory: directoryRef,
      searchTasks: searchTasksRef,
      searchCopy: searchCopyRef,
      texts: textsRef,
      onOpen: onFileMentionOpenRef,
      editable: editableRef,
    }
    const lookup = mentionLabelLookup(refs)
    return {
      extensions: [...createExtensions(placeholderRef, onModEnterRef), ...createMentionExtensions(refs, lookup)],
      labelLookup: lookup,
    }
  }, [])

  const editor = useEditor({
    extensions,
    content: value,
    contentType: "markdown",
    editable,
    autofocus: autoFocus ? "end" : false,
    editorProps: {
      attributes: {
        class: cn(
          "task-description-prosemirror text-base leading-relaxed text-ui-text-primary outline-none",
          variant === "field"
            ? "min-h-[8.5rem] px-3 py-2 sm:text-sm"
            : cn(
                "sm:text-[13px]",
                variant === "inline" && editable && (actions == null ? "min-h-40" : "min-h-[calc(3.25em+1.5rem)] pb-6"),
                // Строка по центру кнопки отправки высотой 1.75rem: первая стоит на месте, последняя — у кнопки.
                variant === "composer" && "py-[calc((1.75rem_-_1.625em)/2)]",
                variant === "studio" && "max-h-60 min-h-24 overflow-y-auto px-2.5 py-2",
              ),
        ),
        spellcheck: "true",
        ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
      },
      handleClick: (view, _pos, event) => openLinkOnModClick(view, event),
      handlePaste: (view, event) => {
        const upload = onPasteFilesRef.current
        if (!upload || !clipboardHasFiles(event.clipboardData)) return false
        const files = filesFromClipboard(event.clipboardData)
        if (files.length === 0) return false
        const request = upload(files)
        if (request) {
          void insertPastedFiles(view, request, uploadingLabelRef.current, (items) => {
            pastedFilesRef.current = [...pastedFilesRef.current, ...items]
            fileMentionsRef.current = withPastedFiles(fileMentionsRef.current, items)
          })
        }
        return true
      },
    },
    onUpdate: ({ editor: next }) => {
      const markdown = next.getMarkdown().trim()
      ensureTasksRef.current?.(taskMentionIds(markdown).tasks)
      // Первая транзакция приходит ещё при создании (TrailingNode дописывает абзац) — это нормализация, а не правка.
      if (emittedRef.current === null) {
        emittedRef.current = markdown
        return
      }
      if (markdown === emittedRef.current) return
      emittedRef.current = markdown
      onChangeRef.current?.(markdown)
    },
    onFocus: () => {
      focusedOnceRef.current = true
      setFocusPending(false)
    },
    onBlur: () => {
      setFocusPending(false)
      onBlurRef.current?.()
    },
    onCreate: ({ editor: created }) => {
      // Markdown не из этого редактора (картинка, чекбокс, сырой HTML) может дать документ вне схемы — пересобираем через HTML.
      try {
        created.state.doc.check()
      } catch {
        created.commands.setContent(created.getHTML(), { emitUpdate: false, contentType: "html" })
        emittedRef.current = created.getMarkdown().trim()
      }
      const markdown = emittedRef.current ?? created.getMarkdown().trim()
      ensureTasksRef.current?.(taskMentionIds(markdown).tasks)
      onBaselineRef.current?.(markdown)
    },
  })

  if (emittedRef.current === null) emittedRef.current = editor.getMarkdown().trim()

  useEffect(() => {
    if (editor.isEditable !== editable) editor.setEditable(editable, false)
  }, [editor, editable])

  useEffect(() => {
    if (!editorHandleRef) return
    editorHandleRef.current = {
      markdown: () => editor.getMarkdown(),
      selectionMarkdown: () => selectionMarkdown(editor),
      replaceSelection: (markdown) => {
        editor.chain().focus().insertContent(markdown, { contentType: "markdown" }).run()
      },
      undo: () => {
        editor.commands.undo()
      },
    }
    return () => {
      editorHandleRef.current = null
    }
  }, [editor, editorHandleRef])

  useEffect(() => {
    if (!insertFilesRef) return
    insertFilesRef.current = (upload) => {
      // В поле, куда ещё не ставили курсор (черновик комментария), ссылка встаёт в конец, а не перед текстом.
      editor.commands.focus(focusedOnceRef.current ? null : "end")
      void insertPastedFiles(editor.view, upload, uploadingLabelRef.current, (items) => {
        pastedFilesRef.current = [...pastedFilesRef.current, ...items]
        fileMentionsRef.current = withPastedFiles(fileMentionsRef.current, items)
      })
    }
    return () => {
      insertFilesRef.current = null
    }
  }, [editor, insertFilesRef])

  useEffect(() => {
    if (!insertTextRef) return
    insertTextRef.current = (text) => {
      editor.commands.focus(focusedOnceRef.current ? null : "end")
      const { from } = editor.state.selection
      const before = from > 1 ? editor.state.doc.textBetween(from - 1, from, "\n", "\n") : ""
      const gap = before !== "" && !/\s/.test(before) ? " " : ""
      editor.commands.insertContent(`${gap}${text}`)
    }
    return () => {
      insertTextRef.current = null
    }
  }, [editor, insertTextRef])

  const fileMentionsKey = JSON.stringify((fileMentions ?? []).map((item) => [item.id, item.label]))
  const mentionsKey = useMemo(
    () => `${taskMentionDirectoryKey(directory)}\u0000${JSON.stringify(mentionTexts)}`,
    [directory, mentionTexts],
  )
  useEffect(() => {
    syncMentionLabels(editor, labelLookup)
  }, [editor, labelLookup, fileMentionsKey, mentionsKey])

  const missingFileLabel = tShot("promptEditor.mention.missingFile")
  useEffect(() => {
    setTaskDescriptionDiff(editor, editable ? undefined : diffBase, (category, id, stateId) => {
      if (category === "person" || category === "task") {
        return taskMentionDisplay(directoryRef.current, category, id, "", textsRef.current).text
      }
      if (isTaskMentionEntityKind(category)) {
        return taskMentionEntityDisplay(directoryRef.current, category, id, stateId, "", textsRef.current).text
      }
      const file = fileMentionsRef.current.find((item) => item.id === id)
      return file ? mentionDisplayLabel("file", file.label) : missingFileLabel
    })
  }, [editor, editable, diffBase, fileMentionsKey, mentionsKey, missingFileLabel])

  const isEmpty = useEditorState({ editor, selector: ({ editor: current }) => current.isEmpty })
  const isFocused = useEditorState({ editor, selector: ({ editor: current }) => current.isFocused })
  const uploading = useEditorState({ editor, selector: ({ editor: current }) => hasPendingFileUploads(current.state) })
  const hidden = !editable && isEmpty && !diffBase

  useEffect(() => {
    onUploadingChange?.(uploading)
  }, [onUploadingChange, uploading])

  if (variant === "field") {
    return (
      <div
        className={cn(
          "flex min-w-0 flex-col rounded-lg border border-ui-border bg-ui-surface-sunken focus-within:ring-2 focus-within:ring-[var(--ui-focus-ring)]",
          className,
          hidden && "hidden",
        )}
      >
        {editable ? <TaskDescriptionToolbar editor={editor} className="border-b border-ui-border p-1" /> : null}
        <EditorContent editor={editor} className="min-w-0" />
      </div>
    )
  }

  const toolbarRevealClass =
    "opacity-0 transition-opacity group-focus-within/description:opacity-100 group-hover/description:opacity-100"

  if (variant === "comment") {
    return (
      <div className={cn("group/description min-w-0", className, hidden && "hidden")}>
        <EditorContent editor={editor} className="min-w-0" />
        {editable ? (
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <TaskDescriptionToolbar editor={editor} className={cn("-ml-1.5", toolbarRevealClass)} />
            {actions ? <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div> : null}
          </div>
        ) : null}
      </div>
    )
  }

  // Колонка студии: тулбар всегда в потоке, поле на всю ширину блока.
  if (variant === "studio") {
    return (
      <div className={cn("flex min-w-0 flex-col overflow-hidden rounded-lg border border-ui-border", className)}>
        {editable ? (
          <TaskDescriptionToolbar
            editor={editor}
            tools={STUDIO_COMMENT_TOOLS}
            className="border-b border-ui-border px-1 py-1"
          />
        ) : null}
        <EditorContent editor={editor} className="min-w-0" />
        {editable && (leading || actions) ? (
          <div className="flex items-center gap-2 px-2 pb-2">
            {leading ? <div className="flex shrink-0">{leading}</div> : null}
            {actions ? <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div> : null}
          </div>
        ) : null}
      </div>
    )
  }

  // Футер панели. Тулбар активного поля (или поля с текстом) открывается полосой поверх ленты: высота футера
  // не меняется, и лента над ним не сдвигается.
  if (variant === "composer") {
    const expanded = isFocused || focusPending || !isEmpty
    return (
      <div className={cn("border-t border-ui-border px-5 py-3", className)}>
        <div className="relative flex min-w-0 items-end gap-2.5">
          {expanded ? (
            <div className="absolute -inset-x-5 bottom-full z-10 flex gap-2.5 border-t border-ui-border bg-ui-bg-elevated px-5 pt-1">
              {leading ? (
                <div aria-hidden className="invisible flex shrink-0">
                  {leading}
                </div>
              ) : null}
              <TaskDescriptionToolbar editor={editor} className="-ml-1.5 min-w-0 flex-1" />
            </div>
          ) : null}
          {leading ? <div className="mt-1 flex shrink-0 self-start">{leading}</div> : null}
          <EditorContent editor={editor} className="it2tv-scrollbar max-h-60 min-w-0 flex-1 overflow-y-auto" />
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </div>
      </div>
    )
  }

  // -mt-3 на всей группе, как у заголовка: на самой липкой панели он затягивает кнопки под край скролла.
  const inlineToolbar = editable ? (
    stickyToolbarClassName ? (
      <div className={cn("sticky top-0 z-20 mb-1 pt-1", stickyToolbarClassName, toolbarRevealClass)}>
        <TaskDescriptionToolbar editor={editor} className="-ml-1.5" />
      </div>
    ) : (
      <TaskDescriptionToolbar editor={editor} className={cn("-mt-3 mb-1 -ml-1.5", toolbarRevealClass)} />
    )
  ) : null

  return (
    <div className={cn("group/description min-w-0", stickyToolbarClassName && "-mt-3", className, hidden && "hidden")}>
      {inlineToolbar}
      <div className="relative min-w-0">
        <EditorContent
          editor={editor}
          className={cn(
            "min-w-0",
            // Зазор под кнопку: она лежит в правом нижнем углу, последняя строка не заходит под неё.
            editable && dictation && "[&_.ProseMirror]:pb-10",
            editable && dictation && actions != null && "[&_.ProseMirror]:min-h-[calc(3.25em+2.5rem)]",
          )}
        />
        {editable && dictation ? (
          // Клик по микрофону не забирает каретку: иначе диктовка всегда дописывается в конец.
          <div className="absolute right-1 bottom-1 z-10" onMouseDown={(event) => event.preventDefault()}>
            <VoiceDictationButton
              projectId={dictation.projectId}
              agentScope={dictation.agentScope}
              onText={(text) => insertDictatedText(editor, text)}
            />
          </div>
        ) : null}
      </div>
      {editable && actions ? (
        <div className={cn("flex items-center gap-2", dictation && "mt-1.5")}>{actions}</div>
      ) : null}
    </div>
  )
}

// Заголовок на месте панели форматирования: в правке его сменяет панель, текст не сдвигается.
export function TaskDescriptionHeading({ children }: { children: ReactNode }) {
  return <div className="-mt-3 mb-1 flex h-7 items-center text-xs leading-4 text-ui-text-muted">{children}</div>
}

const STUDIO_COMMENT_TOOLS = ["bold", "italic", "strike", "bulletList"] as const

type ToolbarAction = {
  id: string
  label: string
  hint?: string
  icon: LucideIcon
  active?: boolean
  danger?: boolean
  run: () => void
}

function TaskDescriptionToolbar({
  editor,
  className,
  tools,
}: {
  editor: Editor
  className?: string
  tools?: readonly string[]
}) {
  const { t } = useTranslation("tasks")
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current.isActive("bold"),
      italic: current.isActive("italic"),
      strike: current.isActive("strike"),
      code: current.isActive("code"),
      heading: current.isActive("heading"),
      bulletList: current.isActive("bulletList"),
      orderedList: current.isActive("orderedList"),
      blockquote: current.isActive("blockquote"),
      table: current.isActive("table"),
    }),
  })

  const command = (apply: (chain: ChainedCommands) => ChainedCommands) => () => {
    apply(editor.chain().focus()).run()
  }

  const groups: ToolbarAction[][] = [
    [
      {
        id: "bold",
        label: t("editor.bold"),
        hint: shortcutHint("Mod", "B"),
        icon: BoldIcon,
        active: state.bold,
        run: command((chain) => chain.toggleBold()),
      },
      {
        id: "italic",
        label: t("editor.italic"),
        hint: shortcutHint("Mod", "I"),
        icon: ItalicIcon,
        active: state.italic,
        run: command((chain) => chain.toggleItalic()),
      },
      {
        id: "strike",
        label: t("editor.strike"),
        hint: shortcutHint("Mod", "Shift", "S"),
        icon: StrikethroughIcon,
        active: state.strike,
        run: command((chain) => chain.toggleStrike()),
      },
      {
        id: "code",
        label: t("editor.code"),
        hint: shortcutHint("Mod", "E"),
        icon: CodeIcon,
        active: state.code,
        run: command((chain) => chain.toggleCode()),
      },
    ],
    [
      {
        id: "heading",
        label: t("editor.heading"),
        hint: shortcutHint("Mod", "Alt", "2"),
        icon: Heading2Icon,
        active: state.heading,
        run: command((chain) => (state.heading ? chain.setParagraph() : chain.setHeading({ level: 2 }))),
      },
      {
        id: "bulletList",
        label: t("editor.bulletList"),
        hint: shortcutHint("Mod", "Shift", "8"),
        icon: ListIcon,
        active: state.bulletList,
        run: command((chain) => chain.toggleBulletList()),
      },
      {
        id: "orderedList",
        label: t("editor.orderedList"),
        hint: shortcutHint("Mod", "Shift", "7"),
        icon: ListOrderedIcon,
        active: state.orderedList,
        run: command((chain) => chain.toggleOrderedList()),
      },
      {
        id: "quote",
        label: t("editor.quote"),
        hint: shortcutHint("Mod", "Shift", "B"),
        icon: TextQuoteIcon,
        active: state.blockquote,
        run: command((chain) => chain.toggleWrap("blockquote")),
      },
    ],
    state.table
      ? [
          {
            id: "addRow",
            label: t("editor.addRow"),
            icon: BetweenHorizontalEndIcon,
            run: command((chain) => chain.addRowAfter()),
          },
          {
            id: "addColumn",
            label: t("editor.addColumn"),
            icon: BetweenVerticalEndIcon,
            run: command((chain) => chain.addColumnAfter()),
          },
          {
            id: "deleteRow",
            label: t("editor.deleteRow"),
            icon: Rows3Icon,
            danger: true,
            run: command((chain) => chain.deleteRow()),
          },
          {
            id: "deleteColumn",
            label: t("editor.deleteColumn"),
            icon: Columns3Icon,
            danger: true,
            run: command((chain) => chain.deleteColumn()),
          },
          {
            id: "deleteTable",
            label: t("editor.deleteTable"),
            icon: Grid2x2XIcon,
            danger: true,
            run: command((chain) => chain.deleteTable()),
          },
        ]
      : [
          {
            id: "table",
            label: t("editor.table"),
            icon: TableIcon,
            run: command((chain) => chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true })),
          },
        ],
  ]

  return (
    <TooltipPrimitive.Provider delayDuration={300} skipDelayDuration={100}>
      <div
        role="toolbar"
        aria-label={t("editor.toolbar")}
        className={cn("flex flex-wrap items-center gap-0.5", className)}
        onMouseDown={(event) => event.preventDefault()}
      >
        {(tools
          ? groups
              .map((group) => group.filter((action) => tools.includes(action.id)))
              .filter((group) => group.length > 0)
          : groups
        ).map((group, index) => (
          <Fragment key={group[0].id}>
            {index > 0 ? <span aria-hidden className="mx-1 h-4 w-px bg-ui-border" /> : null}
            {group.map((action) => (
              <ToolbarButton key={action.id} action={action} />
            ))}
          </Fragment>
        ))}
      </div>
    </TooltipPrimitive.Provider>
  )
}

function ToolbarButton({ action }: { action: ToolbarAction }) {
  const Icon = action.icon
  return (
    <ActionHintTooltip label={action.hint ? `${action.label} · ${action.hint}` : action.label}>
      <button
        type="button"
        tabIndex={-1}
        aria-label={action.label}
        aria-pressed={action.active}
        onClick={action.run}
        className={cn(
          "inline-flex size-7 items-center justify-center rounded-md transition-colors",
          action.active
            ? "bg-ui-surface-hover text-ui-text-primary"
            : "text-ui-text-muted hover:bg-ui-surface-hover hover:text-ui-text-primary",
          action.danger && "hover:text-ui-danger",
        )}
      >
        <Icon className="size-3.5" />
      </button>
    </ActionHintTooltip>
  )
}
